// src/modules/cadete/AvaliacaoCadete.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, query, where, addDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';

// ==================== ÍCONES ====================
const CheckCircleIcon = () => (
    <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const ClockIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const StarIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
);
const BookIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477-4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
);
const ConfettiIcon = () => (
    <svg className="w-20 h-20 text-emerald-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);

const questions = [
    { label: 'CONHECIMENTO DA MATÉRIA', name: 'conhecimento_materia', desc: 'Competência na solução e esclarecimento de dúvidas, deixando evidente que conhece a matéria.' },
    { label: 'DESEMPENHO COMO PROFESSOR', name: 'desempenho_professor', desc: 'Usa as técnicas de ensino e mantém a atenção, bem como o interesse dos alunos.' },
    { label: 'CAPACIDADE DE COMUNICAÇÃO E TRANSMISSÃO DA MATÉRIA', name: 'capacidade_comunicacao', desc: 'Possui boa dicção e usa de uma linguagem adequada ao nível da turma.' },
    { label: 'MOTIVA A PARTICIPAÇÃO DO ALUNO', name: 'motiva_participacao', desc: 'Mantém o interesse dos alunos e relaciona os assuntos às suas aplicações práticas na Instituição.' },
    { label: 'PREPARAÇÃO DAS AULAS', name: 'preparacao_aulas', desc: 'Respeita os horários e o planejamento das aulas.' },
    { label: 'POSTURA EM SALA DE AULA', name: 'postura_sala', desc: 'Correção de atitudes, prima pelo bom exemplo.' },
    { label: 'RELACIONAMENTO INTERPESSOAL COM OS ALUNOS', name: 'relacionamento_interpessoal', desc: 'Respeito aos alunos, à liberdade de expressão e incentivo à participação.' }
];

export const AvaliacaoCadete = () => {
    const { userProfile } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [ciclosAtivos, setCiclosAtivos] = useState([]);
    const [professores, setProfessores] = useState([]);
    const [minhasRespostas, setMinhasRespostas] = useState([]);
    
    const [activeTab, setActiveTab] = useState('pendentes');

    // Modal, Form e Foto Ampliada
    const [showModal, setShowModal] = useState(false);
    const [showThankYouModal, setShowThankYouModal] = useState(false);
    const [selectedProf, setSelectedProf] = useState(null);
    const [selectedCiclo, setSelectedCiclo] = useState(null);
    const [formData, setFormData] = useState({});
    const [comentariosGerais, setComentariosGerais] = useState('');
    const [processing, setProcessing] = useState(false);
    const [zoomedImage, setZoomedImage] = useState(null); // <-- Estado da foto ampliada

    useEffect(() => {
        if (!userProfile?.turma_id) { setLoading(false); return; }

        const qCiclos = query(collection(db, paths.avaliacoesCiclos), where("turma_id", "==", userProfile.turma_id), where("ativo", "==", true));
        const unsubCiclos = onSnapshot(qCiclos, (snap) => setCiclosAtivos(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const unsubProfs = onSnapshot(collection(db, paths.professores), (snap) => setProfessores(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const qResp = query(collection(db, paths.avaliacoesRespostas), where("cadete_id", "==", userProfile.id));
        const unsubResp = onSnapshot(qResp, (snap) => {
            setMinhasRespostas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        });

        return () => { unsubCiclos(); unsubProfs(); unsubResp(); };
    }, [userProfile]);

    const totalPendentes = useMemo(() => {
        let count = 0;
        ciclosAtivos.forEach(ciclo => {
            ciclo.professores_ids.forEach(profId => {
                const jaRespondeu = minhasRespostas.some(r => r.ciclo_id === ciclo.id && r.professor_id === profId);
                if (!jaRespondeu) count++;
            });
        });
        return count;
    }, [ciclosAtivos, minhasRespostas]);

    const getMateriaDoProfessor = (prof, ciclo) => {
        if (!prof.aulas || prof.aulas.length === 0) return 'Matéria não vinculada';

        if (ciclo && ciclo.curso_nome) {
            const aula = prof.aulas.find(a => a.curso_nome === ciclo.curso_nome);
            if (aula) return aula.materia_nome;
        }

        let aula = prof.aulas.find(a => a.curso_id === userProfile?.curso_id);
        if (!aula && userProfile?.curso_nome) {
            aula = prof.aulas.find(a => a.curso_nome === userProfile.curso_nome);
        }

        return aula ? aula.materia_nome : 'Matéria não identificada';
    };

    const handleOpenAvaliacao = (prof, ciclo) => {
        setSelectedProf(prof);
        setSelectedCiclo(ciclo);
        setFormData({});
        setComentariosGerais('');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (Object.keys(formData).length < questions.length) {
            alert("Por favor, avalie todos os quesitos de 1 a 10.");
            return;
        }

        setProcessing(true);
        try {
            await addDoc(collection(db, paths.avaliacoesRespostas), {
                ciclo_id: selectedCiclo.id,
                cadete_id: userProfile.id,
                professor_id: selectedProf.id,
                ...formData,
                comentarios_gerais: comentariosGerais,
                respondido_em: serverTimestamp()
            });
            setShowModal(false);
            
            if (totalPendentes === 1) setShowThankYouModal(true);

        } catch (err) { alert("Erro ao enviar: " + err.message); } 
        finally { setProcessing(false); }
    };

    const handleFinish = () => {
        setShowThankYouModal(false);
        navigate('/'); 
    };

    const handleChange = (name, val) => {
        setFormData(prev => ({ ...prev, [name]: parseInt(val) }));
    };

    const ScaleInput = ({ name, value, onChange }) => {
        return (
            <div className="flex w-full gap-1 mt-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => {
                    const isSelected = value === i;
                    
                    let selectedColor = "";
                    if (i <= 4) selectedColor = "bg-red-500 text-white border-red-600";
                    else if (i <= 6) selectedColor = "bg-orange-400 text-white border-orange-500";
                    else if (i <= 8) selectedColor = "bg-blue-500 text-white border-blue-600";
                    else selectedColor = "bg-emerald-500 text-white border-emerald-600";

                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onChange(name, i)}
                            className={`
                                flex-1 h-12 rounded-lg text-sm font-bold border transition-all duration-200
                                ${isSelected ? selectedColor : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:border-gray-300'}
                                ${isSelected ? 'transform scale-110 shadow-lg z-10' : ''}
                            `}
                        >
                            {i}
                        </button>
                    );
                })}
            </div>
        );
    };

    if (loading) return <LoadingSpinner />;
    if (!userProfile.turma_id) return <div className="p-8 text-center text-gray-500">Seu usuário não está vinculado a uma Turma.</div>;

    let countConcluidas = 0;
    ciclosAtivos.forEach(c => c.professores_ids.forEach(pid => {
        if(minhasRespostas.some(r => r.ciclo_id === c.id && r.professor_id === pid)) countConcluidas++;
    }));

    return (
        <div className="min-h-screen bg-gray-50 pb-12 relative">
            {/* CABEÇALHO */}
            <header className="bg-white border-b border-gray-200 shadow-sm pt-6 pb-6 px-6 mb-8">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    
                    <div className="flex items-center gap-4 text-left w-full md:w-auto">
                        <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão APMBB" className="h-16 w-16 object-contain drop-shadow-sm" />
                        <div>
                            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">Avaliação de Ensino</h1>
                            <p className="text-gray-500 mt-0.5 text-xs md:text-sm font-medium">Sua avaliação confidencial para o contínuo aprimoramento acadêmico.</p>
                        </div>
                    </div>

                    <div className="flex bg-gray-100 p-1.5 rounded-2xl w-full md:w-auto min-w-[280px] shadow-inner mt-4 md:mt-0">
                        <button
                            onClick={() => setActiveTab('pendentes')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${
                                activeTab === 'pendentes'
                                    ? 'bg-white text-blue-700 shadow-[0_2px_10px_rgb(0,0,0,0.08)]'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <ClockIcon /> Pendentes
                            {totalPendentes > 0 && (
                                <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs ml-1">{totalPendentes}</span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('concluidas')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${
                                activeTab === 'concluidas'
                                    ? 'bg-white text-emerald-700 shadow-[0_2px_10px_rgb(0,0,0,0.08)]'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <CheckCircleIcon /> Concluídas
                            {countConcluidas > 0 && (
                                <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs ml-1">{countConcluidas}</span>
                            )}
                        </button>
                    </div>

                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 space-y-8">
                {/* CONTEÚDO */}
                {ciclosAtivos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-16 bg-white rounded-3xl border border-gray-200 shadow-sm text-center">
                        <div className="bg-blue-50 p-6 rounded-full mb-4"><StarIcon className="w-12 h-12 text-blue-300" /></div>
                        <h3 className="text-xl font-bold text-gray-800">Nenhum ciclo ativo</h3>
                        <p className="text-gray-500 mt-2">Aproveite, todas as suas pendências estão em dia.</p>
                    </div>
                ) : (
                    ciclosAtivos.map(ciclo => {
                        const professoresFiltrados = ciclo.professores_ids
                            .map(id => professores.find(p => p.id === id))
                            .filter(prof => {
                                if (!prof) return false;
                                const jaRespondeu = minhasRespostas.some(r => r.ciclo_id === ciclo.id && r.professor_id === prof.id);
                                return activeTab === 'pendentes' ? !jaRespondeu : jaRespondeu;
                            });

                        if (professoresFiltrados.length === 0) return null;

                        return (
                            <div key={ciclo.id} className="animate-fade-in">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className={`h-8 w-2 rounded-full ${activeTab === 'pendentes' ? 'bg-blue-500' : 'bg-emerald-500'}`}></div>
                                    <h2 className="text-2xl font-bold text-gray-800 tracking-tight">{ciclo.titulo}</h2>
                                </div>

                                {/* GRID DE CARDS COM COMPORTAMENTO RESPONSIVO (LISTA NO MOBILE, CARD NO DESKTOP) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                                    {professoresFiltrados.map(prof => {
                                        const materia = getMateriaDoProfessor(prof, ciclo);
                                        const jaRespondeu = activeTab === 'concluidas';
                                        const fotoProf = prof.foto_url || prof.foto;

                                        return (
                                            <div key={prof.id} className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-row md:flex-col justify-between items-center md:items-stretch gap-4 md:gap-0 h-full">
                                                
                                                {/* INFORMAÇÕES DO PROFESSOR */}
                                                <div className="flex flex-row md:flex-col items-center md:items-start gap-3 md:gap-0 md:mb-4 flex-1 min-w-0 w-full">
                                                    
                                                    <div className="flex items-center gap-3 md:gap-4 w-full">
                                                        {/* AVATAR CLICÁVEL COM FOTO OU LETRA */}
                                                        <div 
                                                            onClick={() => fotoProf && setZoomedImage(fotoProf)}
                                                            className={`w-12 h-12 md:w-14 md:h-14 rounded-full md:rounded-2xl flex items-center justify-center text-lg md:text-xl font-black text-white shrink-0 shadow-inner overflow-hidden 
                                                                ${jaRespondeu ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}
                                                                ${fotoProf ? 'cursor-pointer hover:opacity-90 hover:scale-105 transition-all duration-200' : ''}
                                                            `}
                                                            title={fotoProf ? "Clique para ampliar a foto" : ""}
                                                        >
                                                            {fotoProf ? (
                                                                <img src={fotoProf} alt={prof.nome_completo} className="w-full h-full object-cover" />
                                                            ) : (
                                                                prof.nome_completo.charAt(0)
                                                            )}
                                                        </div>
                                                        
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5 md:mb-0.5">{prof.posto}</p>
                                                            <h3 className="font-bold text-gray-900 text-sm md:text-base leading-tight truncate md:whitespace-normal md:line-clamp-2">{prof.nome_completo}</h3>
                                                            
                                                            {/* MATÉRIA MOSTRADA ABAIXO DO NOME NO MOBILE */}
                                                            <div className="md:hidden flex items-center gap-1 text-xs text-gray-500 mt-1">
                                                                <BookIcon />
                                                                <span className="truncate">{materia}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* BLOCO CINZA DE MATÉRIA MOSTRADO APENAS NO DESKTOP */}
                                                    <div className="hidden md:block w-full bg-gray-50 rounded-xl p-3 mt-4 border border-gray-100">
                                                        <div className="flex items-start gap-2 text-sm text-gray-600 font-medium">
                                                            <BookIcon />
                                                            <span className="leading-snug">{materia}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* BOTÃO DE AÇÃO */}
                                                <div className="shrink-0 md:mt-auto w-auto md:w-full">
                                                    {jaRespondeu ? (
                                                        <div className="md:w-full flex items-center justify-center gap-2 py-2 px-3 md:py-3 rounded-xl bg-emerald-50 text-emerald-700 font-bold border border-emerald-100 text-xs md:text-base">
                                                            <CheckCircleIcon /> 
                                                            <span className="hidden md:inline">Avaliação Concluída</span>
                                                        </div>
                                                    ) : (
                                                        <button 
                                                            onClick={() => handleOpenAvaliacao(prof, ciclo)} 
                                                            className="md:w-full flex items-center justify-center gap-1.5 py-2 px-4 md:py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-md hover:shadow-xl transition-all active:scale-95 text-xs md:text-base"
                                                        >
                                                            <span className="hidden md:inline">Iniciar Avaliação</span>
                                                            <span className="md:hidden">Avaliar</span> 
                                                            <StarIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })
                )}

                {/* ESTADOS VAZIOS SECUNDÁRIOS */}
                {activeTab === 'pendentes' && totalPendentes === 0 && ciclosAtivos.length > 0 && (
                    <div className="flex flex-col items-center justify-center p-12 bg-emerald-50 border border-emerald-100 rounded-3xl text-center shadow-sm">
                        <ConfettiIcon />
                        <h3 className="text-2xl font-bold text-emerald-900 mt-4">Missão Cumprida!</h3>
                        <p className="text-emerald-700 mt-2 font-medium">Você completou todas as avaliações deste ciclo.</p>
                    </div>
                )}
            </div>

            {/* MODAL DE AVALIAÇÃO */}
            <Modal show={showModal} onClose={() => setShowModal(false)} title="Avaliação Docente" size="xl">
                <div className="mb-8 flex items-center gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-2xl border border-blue-100">
                    
                    {/* AVATAR CLICÁVEL NO MODAL */}
                    <div 
                        onClick={() => (selectedProf?.foto_url || selectedProf?.foto) && setZoomedImage(selectedProf.foto_url || selectedProf.foto)}
                        className={`w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center text-xl font-black text-white shadow-inner overflow-hidden ${(selectedProf?.foto_url || selectedProf?.foto) ? 'cursor-pointer hover:opacity-90 hover:scale-105 transition-all duration-200' : ''}`}
                        title={(selectedProf?.foto_url || selectedProf?.foto) ? "Clique para ampliar a foto" : ""}
                    >
                        {(selectedProf?.foto_url || selectedProf?.foto) ? (
                            <img src={selectedProf.foto_url || selectedProf.foto} alt={selectedProf.nome_completo} className="w-full h-full object-cover" />
                        ) : (
                            selectedProf?.nome_completo?.charAt(0)
                        )}
                    </div>
                    
                    <div>
                        <h3 className="font-bold text-gray-900 text-xl">{selectedProf?.nome_completo}</h3>
                        <p className="text-blue-700 font-medium mt-0.5 flex items-center gap-1">
                            <BookIcon /> {selectedProf && selectedCiclo ? getMateriaDoProfessor(selectedProf, selectedCiclo) : ''}
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {questions.map((q, idx) => (
                        <div key={idx} className="bg-white">
                            <div className="mb-2">
                                <label className="block text-lg font-bold text-gray-900">
                                    {idx + 1}. {q.label}
                                </label>
                                <p className="text-sm text-gray-500 font-medium mt-1">{q.desc}</p>
                            </div>
                            
                            <ScaleInput name={q.name} value={formData[q.name]} onChange={handleChange} />
                            
                            <div className="flex justify-between text-xs font-bold text-gray-400 mt-3 px-2 uppercase tracking-wider">
                                <span>Péssimo</span>
                                <span>Excelente</span>
                            </div>
                            
                            {/* Linha divisória suave entre as perguntas */}
                            {idx < questions.length - 1 && <hr className="mt-8 border-gray-100" />}
                        </div>
                    ))}

                    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                        <label className="block text-lg font-bold text-gray-900 mb-2">
                            8. COMENTÁRIOS E/OU SUGESTÕES <span className="text-sm text-gray-500 font-medium">(Opcional)</span>
                        </label>
                        <textarea
                            rows="4"
                            value={comentariosGerais}
                            onChange={(e) => setComentariosGerais(e.target.value)}
                            className="w-full bg-white border border-gray-300 rounded-xl p-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none shadow-sm"
                            placeholder="Deixe um elogio, sugestão de melhoria ou observação construtiva..."
                        ></textarea>
                    </div>

                    <div className="flex justify-end gap-3 pt-6">
                        <Button type="button" variant="secondary" onClick={() => setShowModal(false)} disabled={processing} className="px-6 rounded-xl">
                            Cancelar
                        </Button>
                        <Button type="submit" variant="primary" className="px-8 rounded-xl font-bold text-base bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all" disabled={processing}>
                            {processing ? 'Enviando...' : 'Confirmar Avaliação'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* MODAL DE SUCESSO */}
            <Modal show={showThankYouModal} onClose={handleFinish} title="" size="md">
                <div className="text-center p-6">
                    <ConfettiIcon />
                    <h3 className="text-3xl font-black text-gray-900 mt-6 mb-3">Obrigado!</h3>
                    <p className="text-gray-600 text-lg mb-8 font-medium">
                        Suas avaliações foram enviadas com sucesso e ajudarão a melhorar o corpo docente.
                    </p>
                    <Button onClick={handleFinish} variant="primary" className="w-full py-4 text-lg rounded-xl font-bold shadow-lg">
                        Voltar ao Início
                    </Button>
                </div>
            </Modal>

            {/* LIGHBOX DA FOTO (FICA POR CIMA DE TUDO) */}
            {zoomedImage && (
                <div 
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in" 
                    onClick={() => setZoomedImage(null)}
                >
                    <button 
                        className="absolute top-6 right-6 text-white/80 hover:text-white bg-black/40 hover:bg-black/80 p-3 rounded-full transition-all"
                        onClick={() => setZoomedImage(null)}
                        title="Fechar"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <img 
                        src={zoomedImage} 
                        alt="Foto Ampliada" 
                        className="max-w-full max-h-[90vh] rounded-2xl shadow-2xl object-contain transform transition-transform" 
                        onClick={(e) => e.stopPropagation()} 
                    />
                </div>
            )}
        </div>
    );
};